import { AxiosResponse, AxiosError } from "axios"
import ConnectApi from "../../baseService"
import { TApiResponse } from "../../TApiResponse"
import {
  TInvoiceMakeListGetRes,
  TInvoiceMakeListGetReq,
  TInvoiceSeikyuSimePostReq,
  TInvoicePrintSeikyusyoReq,
  TInvoicePrintGoukeiSeikyusyoReq,
  TInvoiceSeikyuSimeCheckListGetRes,
  TInvoiceSeikyusyoCheckListGetRes,
} from "./TInvoice"

/**
 * 請求合計一覧画面に対するAPIリクエスト用サービス
 *
 * @export
 * @class MenuService
 * @extends {ConnectApi}
 */
export default class MakeInvoiceService extends ConnectApi {
  /**
   * 請求合計一覧取得
   *
   * @param {TInvoiceMakeListGetReq} params
   * @param {(param: TApiResponse<TInvoiceMakeListGetRes[]>) => any} resolve
   * @param {(param: TApiResponse<TInvoiceMakeListGetRes[]>) => any} reject
   * @memberof makeInvoiceService
   */
  getList = async (
    params: TInvoiceMakeListGetReq,
    resolve?: (param: TApiResponse<TInvoiceMakeListGetRes[]>) => any,
    reject?: (param: TApiResponse<TInvoiceMakeListGetRes[]> | undefined) => any
  ) => {
    try {
      //API呼出
      const res: AxiosResponse<TApiResponse<TInvoiceMakeListGetRes[]>> =
        await this.getInner(`/billing/seikyugoukei`, params)
      return res.data
    } catch (err) {
      if (typeof err !== "object" || err == null) throw err
      const error = err as TApiResponse<TInvoiceMakeListGetRes[]>
      throw reject ? reject(error) : error
    }
  }

  /**
   * 請求締め処理実行前チェック
   *
   * @param {TInvoiceSeikyuSimePostReq} params
   * @param {(param: TApiResponse<TInvoiceSeikyuSimeCheckListGetRes[]>) => any} resolve
   * @param {(param: TApiResponse<TInvoiceSeikyuSimeCheckListGetRes[]>) => any} reject
   * @memberof makeInvoiceService
   */
  getCheckList = async (
    params: TInvoiceSeikyuSimePostReq,
    resolve?: (param: TApiResponse<TInvoiceSeikyuSimeCheckListGetRes[]>) => any,
    reject?: (
      param: TApiResponse<TInvoiceSeikyuSimeCheckListGetRes[]> | undefined
    ) => any
  ) => {
    try {
      // getでは大量のarrayに耐えられないため、内部はpostに変更
      //API呼出
      const res: AxiosResponse<
        TApiResponse<TInvoiceSeikyuSimeCheckListGetRes[]>
      > = await this.postInner(`/billing/seikyusimechecklist`, params)
      return res.data
    } catch (err) {
      if (typeof err !== "object" || err == null) throw err
      const error = err as TApiResponse<TInvoiceSeikyuSimeCheckListGetRes[]>
      throw reject ? reject(error) : error
    }
  }

  /**
   * 請求書出力処理実行前チェック
   *
   * @param {TInvoiceSeikyuSimePostReq} params
   * @param {(param: TApiResponse<TInvoiceSeikyusyoCheckListGetRes[]>) => any} resolve
   * @param {(param: TApiResponse<TInvoiceSeikyusyoCheckListGetRes[]>) => any} reject
   * @memberof makeInvoiceService
   */
  getSeikyusyoCheckList = async (
    params: TInvoiceSeikyuSimePostReq,
    resolve?: (param: TApiResponse<TInvoiceSeikyusyoCheckListGetRes[]>) => any,
    reject?: (
      param: TApiResponse<TInvoiceSeikyusyoCheckListGetRes[]> | undefined
    ) => any
  ) => {
    try {
      //API呼出
      const res: AxiosResponse<
        TApiResponse<TInvoiceSeikyusyoCheckListGetRes[]>
      > = await this.getInner(`/billing/seikyusyochecklist`, params)
      return res.data
    } catch (err) {
      if (typeof err !== "object" || err == null) throw err
      const error = err as TApiResponse<TInvoiceSeikyusyoCheckListGetRes[]>
      throw reject ? reject(error) : error
    }
  }

  /**
   * 請求締め処理
   *
   * @param {TInvoiceSeikyuSimePostReq} params
   * @param {(param: TApiResponse<TInvoiceSeikyuSimePostRes[]>) => any} resolve
   * @param {(param: TApiResponse<TInvoiceSeikyuSimePostRes[]>) => any} reject
   * @memberof makeInvoiceService
   */
  seikyuSime = async (params: TInvoiceSeikyuSimePostReq) => {
    try {
      //API呼出
      await this.postInner(`/billing/seikyusime`, params)
    } catch (err) {
      throw err
    }
  }

  /**
   * 請求書印刷（表紙／明細別）
   *
   * @param {TInvoicePrintSeikyusyoReq} params
   * @memberof makeInvoiceService
   */
  printSeikyusyo = async (
    params: TInvoicePrintSeikyusyoReq,
    resolve?: (param: TApiResponse<Blob>) => any,
    reject?: (param: AxiosError<TApiResponse<Blob>>) => any
  ) => {
    try {
      //API呼出
      const res: AxiosResponse<Blob> = await this.postInner(
        `/report/CreateReport/G060102`,
        params
      )
      return res.data
    } catch (err) {
      throw err
    }
  }

  /**
   * 請求書印刷（表紙明細合体版）
   *
   * @param {TInvoicePrintSeikyusyoReq} params
   * @memberof makeInvoiceService
   */
  printSeikyusyoGattaiban = async (
    params: TInvoicePrintSeikyusyoReq,
    resolve?: (param: TApiResponse<Blob>) => any,
    reject?: (param: AxiosError<TApiResponse<Blob>>) => any
  ) => {
    try {
      //API呼出
      const res: AxiosResponse<Blob> = await this.postInner(
        `/report/CreateReport/G060103`,
        params
      )
      return res.data
    } catch (err) {
      throw err
    }
  }

  /**
   * 合計請求書印刷
   *
   * @param {TInvoicePrintGoukeiSeikyusyoReq} params
   * @memberof makeInvoiceService
   */
  printGoukeiSeikyusyo = async (
    params: TInvoicePrintGoukeiSeikyusyoReq,
    resolve?: (param: TApiResponse<Blob>) => any,
    reject?: (param: AxiosError<TApiResponse<Blob>>) => any
  ) => {
    try {
      //API呼出
      const res: AxiosResponse<Blob> = await this.postInner(
        `/report/CreateReport/G060104`,
        params
      )
      return res.data
    } catch (err) {
      throw err
    }
  }
}
